import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Output,
  ViewChild,
} from '@angular/core';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor } from '@freelancer/ui/link';
import { CalloutColor } from './callout-color';
import { CalloutPlacement } from './callout-placement';
import { CalloutSize } from './callout-size';

// TODO: Need animations for fade-in-out.
// Ref: TODO
@Component({
  selector: 'fl-callout-content',
  template: `
    <div
      class="CalloutBody WebappCompatWrapper"
      #calloutBody
      (click)="handleCalloutBodyClick()"
      (mouseenter)="handleCalloutBodyMouseenter()"
      (mouseleave)="handleCalloutBodyMouseleave()"
      [style.top.px]="topPosition"
      [style.left.px]="leftPosition"
      [style.zIndex]="zIndex"
      [attr.data-edge-to-edge]="edgeToEdge"
      [attr.data-size]="size"
      [attr.data-color]="color"
      [attr.data-hide-arrow]="!shouldShowArrow"
      [attr.data-mobile-fullscreen]="mobileFullscreen"
    >
      <div
        class="CalloutArrow"
        *ngIf="shouldShowArrow"
        role="presentation"
        [style.top.px]="arrowTopPosition"
        [style.left.px]="arrowLeftPosition"
        [attr.data-placement]="placement"
        [attr.data-color]="color"
      ></div>
      <fl-link
        class="CloseButton"
        *ngIf="shouldShowCloseButton"
        color="LinkColor.INHERIT"
        (click)="handleCloseButtonClick()"
      >
        <fl-icon
          name="ui-close"
          [color]="IconColor.INHERIT"
          [size]="IconSize.SMALL"
        ></fl-icon>
      </fl-link>
      <div class="CalloutContent"><ng-content></ng-content></div>
    </div>
  `,
  styleUrls: ['./callout-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutContentComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;

  arrowTopPosition?: number;
  arrowLeftPosition?: number;
  topPosition?: number;
  leftPosition?: number;
  zIndex?: number;

  edgeToEdge: boolean;
  hideArrow: boolean;
  hideCloseButton: boolean;
  hover: boolean;
  mobileFullscreen: boolean;
  placement: CalloutPlacement;
  size?: CalloutSize;

  isMobile: boolean;
  shouldShowArrow: boolean;
  shouldShowCloseButton: boolean;

  @HostBinding('attr.data-color') color: CalloutColor;

  @Output() bodyClick = new EventEmitter<void>();
  @Output() bodyMouseenter = new EventEmitter<void>();
  @Output() bodyMouseleave = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @ViewChild('calloutBody') calloutBody: ElementRef<HTMLDivElement>;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  getElementBoundingRect() {
    return this.calloutBody.nativeElement.getBoundingClientRect();
  }

  handleCalloutBodyClick() {
    this.bodyClick.emit();
  }

  handleCalloutBodyMouseenter() {
    if (!this.isMobile) {
      this.bodyMouseenter.emit();
    }
  }

  handleCalloutBodyMouseleave() {
    if (!this.isMobile) {
      this.bodyMouseleave.emit();
    }
  }

  handleCloseButtonClick() {
    this.close.emit();
  }

  setOptions(options: {
    color: CalloutColor;
    edgeToEdge: boolean;
    mobileFullscreen: boolean;
    hideArrow: boolean;
    hideCloseButton: boolean;
    hover: boolean;
    size?: CalloutSize;
  }) {
    this.color = options.color;
    this.edgeToEdge = options.edgeToEdge;
    this.hideArrow = options.hideArrow;
    this.hideCloseButton = options.hideCloseButton;
    this.hover = options.hover;
    this.mobileFullscreen = options.mobileFullscreen;
    this.size = options.size;
  }

  setPosition(
    top?: number,
    left?: number,
    arrowTop?: number,
    arrowLeft?: number,
  ) {
    this.topPosition = top;
    this.leftPosition = left;
    this.arrowTopPosition = arrowTop;
    this.arrowLeftPosition = arrowLeft;

    this.setMobileOptions();

    this.changeDetectorRef.markForCheck();
  }

  setPlacement(placement: CalloutPlacement) {
    this.placement = placement;
    this.changeDetectorRef.markForCheck();
  }

  private setMobileOptions() {
    this.isMobile =
      window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);

    this.shouldShowArrow = !this.hideArrow;
    this.shouldShowCloseButton = !this.hideCloseButton && !this.hover;

    // Mobile overrides to standardize dialog UIs
    if (this.isMobile) {
      this.shouldShowArrow = false;
      this.shouldShowCloseButton =
        this.mobileFullscreen || this.shouldShowCloseButton;
    }
  }
}
